<template>
    <slot>
        <section class="accordion">
            <div class="accordion-header">
                <slot name="header" />
            </div>

            <div class="accordion-content">
                <slot />
            </div>

            <div class="accordion-footer">
                <slot name="footer" />
            </div>
        </section>
    </slot>
</template>

<script setup>
    import { provide, ref, watch, toRef } from 'vue';

    const props = defineProps({
        onOpen        : Function,
        onBeforeOpen  : Function,
        onClose       : Function,
        onBeforeClose : Function,
        onToggle      : Function,

        disabled: Boolean,

        multiOpen: {
            type    : Boolean,
            default : false
        },

        disableClose: {
            type    : Boolean,
            default : false
        },

        allClosed: {
            type    : Boolean,
            default : false
        },
        
        idPrefix: {
            type    : String,
            default : 'accordion'
        },

        modelValue: {
            type: [Number, String, Array],
        }
    })

    const emit = defineEmits(['update:modelValue'])

    const loading = ref(false)

    const active = ref([])

    /**
     * Keep track of the 'active' accordion item(s) when updated from the parent
     */
    watch(
        () => props.modelValue, 
        (value) => Array.isArray(value) 
            ? active.value = value
            : active.value = [value],
        {
            immediate: true
        }
    )

    /**
     * Keep track of the 'active' accordion item(s) when updated from the child (item itself)
     */
    watch(
        active,
        (value) => emit(
            'update:modelValue', 
            props.multiOpen
                ? value
                : value[0] ?? null
        )
    )

    /**
     * Is the current item active (open) currently
     * 
     * @param {String|Number} id 
     */
    const isActive = id => active.value.indexOf(id) !== -1

    /**
     * Toggles the current item
     *  (opens if closes, closes if open, otherwise ignored)
     * 
     * @param {String|Number} id 
     * @param {Boolean}       force
     */
    const toggleItem = async (id, force = false) => {
        //If disabled we can't interact
        if(props.disabled && !force){
            return
        }

        loading.value = true

        if(!await callEvents('onToggle', id)){
            loading.value = false
            
            return false
        }

        //Item is active
        if(!isActive(id)){
            await openItem(id)
        } else {
            await closeItem(id)
        }

        loading.value = false
    }

    /**
     * Activates an item if its not already active
     * 
     * @param {String|Number} id 
     * @param {Boolean}       force
     */
    const openItem = async (id, force = false) => {
        //If disabled we can't interact
        if(props.disabled && !force){
            return
        }

        //Our item is already open
        if(isActive(id)){
            return
        }

        if(!await callEvents('onBeforeOpen', id)){
            return false
        }

        //If we don't allow multi-open remove all other active items
        if(!props.multiOpen){

            //We're simulating close for all active items without physically closing them
            //(this allows the events to run without breaking functionality of transitions)
            for(const currentlyActive of active.value)
            {
                if(!await callEvents('onBeforeClose', currentlyActive)){
                    return false
                }

                if(!await callEvents('onClose', currentlyActive)){
                    return false
                }
            }

            
            active.value = [id]
        } else {
            active.value.push(id)
        }

        if(!await callEvents('onOpen', id)){
            return false
        }
    }

    /**
     * Deactivates an item if its not already closed
     * 
     * @param {String|Number} id 
     * @param {Boolean}       force
     */
    const closeItem = async (id, force = false) => {
        //If disabled we can't interact
        if(props.disabled && !force){
            return
        }

        if(props.disableClose){
            return
        }

        //Our item is already closed
        if(!isActive(id)){
            return
        }

        //The item we're trying to close is the only open item 
        //and we aren't allowing no items to be open
        if((active.value.length === 1 && !props.allClosed)){
            return
        }

        if(!await callEvents('onBeforeClose', id)){
            return false
        }

        active.value.splice(
            active.value.indexOf(id), 
            1
        )

        if(!await callEvents('onClose', id)){
            return false
        }
    }

    /**
     * Calls a series of events for a given state of the accordion
     * 
     * @param {String} event 
     * @param {String|Number} id 
     */
    const callEvents = async function(event, id = undefined){
        let success = true

        if(props[event] instanceof Function){
            success = await props[event](id) !== false
        }

        if(events.value[id]?.[event] instanceof Function){
            success = await events.value[id][event]() !== false
        }

        return success
    }

    /**
     * Contains all events used by the accordion items
     */
    const events = ref({})

    /**
     * Adds events to a given item id
     * 
     * @param {String|Number} id 
     * @param {Object<String, Function>} callbacks 
     */
    const setEvents = (id, callbacks) => {
        events.value[id] = callbacks
    }

    /**
     * Removes all events for a given item
     * 
     * @param {String|Number} id 
     */
    const removeEvents = id => delete events.value[id]

    const returns = {
        loading      : loading,
        disabled     : toRef(props, 'disabled'),
        multiOpen    : toRef(props, 'multiOpen'),
        toggleItem   : toggleItem,
        openItem     : openItem,
        closeItem    : closeItem,
        isActive     : isActive,
        setEvents    : setEvents,
        removeEvents : removeEvents,
    }

    /**
     * Allow the items to maintain state correctly
     */
    provide('accordion', returns)

    defineExpose(returns)
</script>